import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AuthCallback() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [err, setErr] = useState(null);
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        (async () => {
            // The backend's /api/auth/google/callback already set the session
            // cookie via a server-side redirect before this page ever loaded --
            // just load it into the shared auth context and route by role.
            try {
                const r = await api.get("/auth/me");
                // Load the new session into the shared auth context BEFORE navigating,
                // otherwise ProtectedRoute still sees user=null and bounces to /login (login loop).
                await refresh();
                const employee = r.data.employee;
                if (!employee) {
                    navigate("/access-pending", { replace: true, state: { user: r.data.user } });
                } else if (employee.role === "admin") {
                    navigate("/admin", { replace: true, state: { user: r.data.user } });
                } else if (employee.role === "manager") {
                    navigate("/foreman", { replace: true, state: { user: r.data.user } });
                } else {
                    navigate("/crew", { replace: true, state: { user: r.data.user } });
                }
            } catch (e) {
                setErr(e?.response?.data?.detail || "Sign-in failed.");
            }
        })();
        // navigate is stable; api is a stable module-level singleton — intentional omission
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center paper-bg" data-testid="auth-callback">
            <div className="bg-white rounded-2xl shadow-card border border-brand-charcoal/15 p-8 max-w-md text-center">
                <h2 className="font-bold text-2xl text-brand-charcoal">Signing you in…</h2>
                {!err && <p className="text-brand-charcoal/60 mt-2 text-sm">Verifying your access.</p>}
                {err && (
                    <>
                        <p className="text-brand-danger mt-3 text-sm" data-testid="auth-error">{err}</p>
                        <button onClick={() => (window.location.href = "/login")} className="mt-4 text-brand-navy underline underline-offset-4 text-sm">
                            Back to sign in
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
